import { get, set } from 'lodash';

import { FIELD_PATHS, FIELD_VALUES } from '@funnels/constants';
import { HomeLoanLiability, Liability, PropertyAsset } from '@funnels/types';

import { INITIAL_HOME_LOAN_LIABILITY } from './initialFormValues';

/**
 * @param newPrimaryPurpose
 * @param property
 * @param liabilities
 * @param hardReset when hardReset is equal to true, the reset data will directly save to backend, use case TV properties move to dropdowns
 * when hardReset is equal to false, you can still tweak the related data afterwards, use case TV property page property type change
 * @returns
 */
export default function resetProperty(
  newPrimaryPurpose: FIELD_VALUES,
  property: PropertyAsset,
  liabilities: Liability[],
  hardReset: boolean = false
) {
  const assetClientId = get({ assets: [property] }, FIELD_PATHS.ASSET_CLIENT_ID(0));
  const assetOwnership = get({ assets: [property] }, FIELD_PATHS.ASSET_PERCENT_OWNED(0));
  const propertyStatus = get({ assets: [property] }, FIELD_PATHS.ASSET_PROPERTY_STATUS(0));
  const toBePurchased = get({ assets: [property] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0));
  const usedAsSecurity = get({ assets: [property] }, FIELD_PATHS.ASSET_USED_AS_SECURITY(0));
  const isKnockdownRebuild = propertyStatus === FIELD_VALUES.VACANT_LAND && !toBePurchased;
  const isConstruction = get({ assets: [property] }, FIELD_PATHS.ASSET_IS_CONSTRUCTION(0));
  const primaryPurpose = !usedAsSecurity
    ? FIELD_VALUES.OTHER
    : toBePurchased
    ? FIELD_VALUES.NEW_PURCHASE
    : FIELD_VALUES.REFINANCE;
  const newAsset = { ...property };
  let newLiabilities = liabilities;
  const hasPropertyLiability = newLiabilities.some((liability) => liability.assetClientId.includes(assetClientId));

  // REFI
  if (newPrimaryPurpose === FIELD_VALUES.REFINANCE) {
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_USED_AS_SECURITY(0), true);
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), false);

    // make sure for refinance, there is at least 1 loan split
    if (!hasPropertyLiability) {
      const liability = INITIAL_HOME_LOAN_LIABILITY();
      liability.assetClientId.push(assetClientId);
      if (assetOwnership) {
        liability.percentOwned = { ...assetOwnership };
      }
      liability.clearingFromThisLoan = true;
      newLiabilities.push(liability);
    }

    // NP -> Refi
    if (primaryPurpose === FIELD_VALUES.NEW_PURCHASE) {
      if ([FIELD_VALUES.ESTABLISHED, FIELD_VALUES.NEW_BUILDING].includes(propertyStatus)) {
        set({ assets: [newAsset] }, FIELD_PATHS.ASSET_PROPERTY_STATUS(0), FIELD_VALUES.ESTABLISHED);
      }
    }

    // toBeSold should not be set on property assets which primary purpose is not Other
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_SOLD(0), undefined);
  }

  // NP
  else if (newPrimaryPurpose === FIELD_VALUES.NEW_PURCHASE) {
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_USED_AS_SECURITY(0), true);
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), true);

    if (!isKnockdownRebuild) {
      set({ assets: [newAsset] }, FIELD_PATHS.ASSET_PROPERTY_STATUS(0), FIELD_VALUES.ESTABLISHED);
    }

    // clean up construction values as they are unused in New Purchase
    if (isConstruction) {
      set({ assets: [newAsset] }, FIELD_PATHS.ASSET_IS_CONSTRUCTION(0), undefined);
      set({ assets: [newAsset] }, FIELD_PATHS.ASSET_CONSTRUCTION_DETAILS(0), undefined);
    }
    //since we remove all related liabilities, we don't need to update clearingFromOtherSource
    newLiabilities = liabilities.filter((liability) => !liability.assetClientId.includes(assetClientId));

    // toBeSold should not be set on property assets which primary purpose is not Other
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_SOLD(0), undefined);
  }

  // SECURITY_PROPERTY
  else if (newPrimaryPurpose === FIELD_VALUES.SECURITY_PROPERTY) {
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_USED_AS_SECURITY(0), true);
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), false);

    // NP -> SP
    if (primaryPurpose === FIELD_VALUES.NEW_PURCHASE) {
      if ([FIELD_VALUES.ESTABLISHED, FIELD_VALUES.NEW_BUILDING].includes(propertyStatus)) {
        set({ assets: [newAsset] }, FIELD_PATHS.ASSET_PROPERTY_STATUS(0), FIELD_VALUES.ESTABLISHED);
      }
    }

    // toBeSold should not be set on property assets which primary purpose is not Other
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_SOLD(0), undefined);
  }

  // OTHER
  else {
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_USED_AS_SECURITY(0), false);
    set({ assets: [newAsset] }, FIELD_PATHS.ASSET_TO_BE_PURCHASED(0), false);

    // Np -> Other
    if (primaryPurpose === FIELD_VALUES.NEW_PURCHASE) {
      if ([FIELD_VALUES.ESTABLISHED, FIELD_VALUES.NEW_BUILDING].includes(propertyStatus)) {
        set({ assets: [newAsset] }, FIELD_PATHS.ASSET_PROPERTY_STATUS(0), FIELD_VALUES.ESTABLISHED);
      }
    }
  }

  // liabilities handlings for generic scenarios
  // case 1: clearingFromThisLoan tweaks
  if ([FIELD_VALUES.REFINANCE, FIELD_VALUES.SECURITY_PROPERTY, FIELD_VALUES.OTHER].includes(newPrimaryPurpose)) {
    newLiabilities = liabilities.map((liability) => {
      if (!liability.assetClientId.includes(assetClientId)) {
        return liability;
      }

      const newLiability = { ...liability };
      const isClearingFromThisLoan = newPrimaryPurpose === FIELD_VALUES.REFINANCE;

      set({ liabilities: [newLiability] }, FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(0), isClearingFromThisLoan);

      return newLiability;
    });
  }
  // case 2: clean up toBeToppedUp
  if (hardReset && [FIELD_VALUES.REFINANCE, FIELD_VALUES.OTHER].includes(newPrimaryPurpose)) {
    newLiabilities = newLiabilities.map((liability) => {
      // it should be newLiabilities, cuz using liabilities will lose all update of case 1
      if (!liability.assetClientId.includes(assetClientId)) {
        return liability;
      }
      const newLiability = { ...liability };
      set({ liabilities: [newLiability] }, FIELD_PATHS.LIABILITY_TO_BE_TOPPED_UP(0), undefined);
      return newLiability;
    });
  }
  // case 3: clean up clearingFromOtherSource
  if (
    [FIELD_VALUES.REFINANCE, FIELD_VALUES.SECURITY_PROPERTY, FIELD_VALUES.OTHER].includes(newPrimaryPurpose) &&
    hasPropertyLiability
  ) {
    newLiabilities = newLiabilities.map((liability) => {
      if (!liability.assetClientId.includes(assetClientId)) {
        return liability;
      }
      const newLiability = { ...liability };
      // update clearingFromOtherSource to PAYING_OUT for liabilities if property is other and toBeSold is true
      if (newPrimaryPurpose === FIELD_VALUES.OTHER && newAsset.toBeSold) {
        set(
          { liabilities: [newLiability] },
          FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(0),
          FIELD_VALUES.PAYING_OUT
        );
      } else {
        // clean clearingFromOtherSource for liabilities if property is other and toBeSold is false or property is refi or security
        set({ liabilities: [newLiability] }, FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(0), undefined);
      }
      return newLiability;
    });
  }
  // case 4: update property's liabilities' newLimit
  // propertyType !== Other && clearingFromThisLoan === false ---- newLimit = creditLimit
  // propertyType !== Other && clearingFromThisLoan === true ---- newLimit = 0
  // propertyType === Other && toBeSold === true ---- newLimit = creditLimit
  // propertyType === Other && toBeSold === false ---- newLimit = 0
  if (
    [FIELD_VALUES.REFINANCE, FIELD_VALUES.SECURITY_PROPERTY, FIELD_VALUES.OTHER].includes(newPrimaryPurpose) &&
    hasPropertyLiability
  ) {
    newLiabilities = newLiabilities.map((liability) => {
      if (!liability.assetClientId.includes(assetClientId)) {
        return liability;
      }
      const newLiability = { ...liability };
      if (
        (newPrimaryPurpose !== FIELD_VALUES.OTHER && !(liability as HomeLoanLiability).clearingFromThisLoan) ||
        (newPrimaryPurpose === FIELD_VALUES.OTHER && !newAsset.toBeSold)
      ) {
        const creditLimit = get({ liabilities: [newLiability] }, FIELD_PATHS.LIABILITY_CREDIT_LIMIT(0));
        set({ liabilities: [newLiability] }, FIELD_PATHS.LIABILITY_NEW_LIMIT(0), creditLimit);
      } else {
        set({ liabilities: [newLiability] }, FIELD_PATHS.LIABILITY_NEW_LIMIT(0), 0);
      }
      return newLiability;
    });
  }

  return { property: newAsset, liabilities: newLiabilities };
}
